import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

export type ImplantSizeWord = 'subtle' | 'moderate' | 'medium' | 'default' | 'strong' | 'high';
export type ImplantSize = 1 | 2 | 3;

export interface ChestImageInput {
  /**
   * Reference image data.
   * Server accepts raw base64 OR a full data URL string.
   */
  imageBase64: string;
  mimeType: string;

  /**
   * If provided, server uses this prompt as-is and ignores size selection logic.
   */
  prompt?: string;

  /**
   * Preferred key for the server-side lambda:
   * size can be 1-3 or a word mapping:
   * subtle -> 1
   * moderate/medium/default -> 2
   * strong/high -> 3
   *
   * Default (if missing) = 2
   */
  size?: ImplantSize | ImplantSizeWord;

  /**
   * Aliases supported by the server lambda for compatibility:
   * - intensity
   * - treatment
   * If `size` is not provided, we will fall back to these.
   */
  intensity?: ImplantSize | ImplantSizeWord;
  treatment?: ImplantSize | ImplantSizeWord;

  /**
   * Optional override templates (must be length >= 3 to be used server-side).
   * Index mapping:
   * 0 -> size 1 (conservative)
   * 1 -> size 2 (moderate)
   * 2 -> size 3 (fuller)
   */
  prompts?: string[];
}

export interface ChestRequestBody {
  prompt?: string;
  size?: number | string;
  intensity?: number | string;
  treatment?: number | string;
  prompts?: string[];
  image: {
    data: string;
    mimeType: string;
  };
}

export interface ChestResponse {
  imageBase64?: string;
  model?: string;

  // server echoes these (it may include all three for compatibility)
  size?: number;
  intensity?: number;
  treatment?: number | null;

  error?: string;
  message?: string;
  details?: any;
}

@Injectable({ providedIn: 'root' })
export class ChestService {
  private readonly endpoint =
    'https://cvcfovz6a6h7dixiamaxn56ske0nfclf.lambda-url.us-east-1.on.aws/';

  constructor(private http: HttpClient) {}

  /**
   * Main entry: provide a reference image plus either:
   *  - prompt (custom)
   *  - OR size (1-3 or mapped word) [preferred]
   *
   * The server defaults to size=2 when size/intensity/treatment are missing.
   * Returns a data URL (data:<mime>;base64,...) string.
   */
  async generateChestPreview(input: ChestImageInput): Promise<string> {
    const body = this.buildRequestBody(input);

    const response = await firstValueFrom(
      this.http.post<ChestResponse>(this.endpoint, body, {
        headers: this.buildHeaders(),
      })
    );

    if (response?.error) {
      const msg = response.message ? `${response.error}: ${response.message}` : response.error;
      throw new Error(msg);
    }

    if (!response?.imageBase64) {
      throw new Error('The AI provided a response but no image was found.');
    }

    return response.imageBase64;
  }

  /**
   * Backwards-compatible helper: custom prompt mode.
   * (Server will use prompt and ignore size logic.)
   */
  async editImage(base64Image: string, mimeType: string, prompt: string): Promise<string> {
    return this.generateChestPreview({
      imageBase64: base64Image,
      mimeType,
      prompt,
    });
  }

  /**
   * Primary helper: choose implant visualization size (1..3).
   * Accepts numeric or word; client normalizes to numeric before sending.
   */
  async applySize(base64Image: string, mimeType: string, size?: ImplantSize | ImplantSizeWord): Promise<string> {
    return this.generateChestPreview({
      imageBase64: base64Image,
      mimeType,
      size,
    });
  }

  /**
   * Compatibility helper: if existing UI calls it "intensity" or "treatment"
   * we still map it to the lambda’s size selection logic.
   */
  async applyIntensity(base64Image: string, mimeType: string, intensity?: ImplantSize | ImplantSizeWord): Promise<string> {
    return this.generateChestPreview({
      imageBase64: base64Image,
      mimeType,
      intensity,
    });
  }

  async applyTreatment(base64Image: string, mimeType: string, treatment?: ImplantSize | ImplantSizeWord): Promise<string> {
    return this.generateChestPreview({
      imageBase64: base64Image,
      mimeType,
      treatment,
    });
  }

  // =========================
  // Request construction
  // =========================

  private buildRequestBody(input: ChestImageInput): ChestRequestBody {
    const imageData = this.stripDataUrlIfPresent(input.imageBase64);

    const body: ChestRequestBody = {
      image: {
        data: imageData,
        mimeType: input.mimeType || 'image/png',
      },
    };

    // If prompt provided, use it as-is (server treats size fields as optional/ignored)
    const prompt = (input.prompt || '').trim();
    if (prompt) {
      body.prompt = prompt;

      // Pass prompts override if provided (server will accept, though it won't be used when prompt is set)
      if (Array.isArray(input.prompts) && input.prompts.length) {
        body.prompts = input.prompts;
      }

      return body;
    }

    // Determine size using server’s precedence: size -> intensity -> treatment -> default(2)
    const rawSize = input.size ?? input.intensity ?? input.treatment;

    if (rawSize !== undefined && rawSize !== null) {
      body.size = this.normalizeSize(rawSize);
    } else {
      body.size = 2; // server default behavior
    }

    // Optional override templates (server uses only if length >= 3)
    if (Array.isArray(input.prompts) && input.prompts.length) {
      body.prompts = input.prompts;
    }

    return body;
  }

  /**
   * Mirrors server mapping for size selection:
   * subtle -> 1
   * moderate/medium/default -> 2
   * strong/high -> 3
   * Also accepts numeric 1-3; clamps to 1-3.
   */
  private normalizeSize(v: ImplantSize | ImplantSizeWord): ImplantSize {
    if (typeof v === 'number') {
      return this.clampSize(v);
    }

    const s = String(v || '').trim().toLowerCase();

    // if s in ("subtle",): return 1
    if (s === 'subtle') return 1;

    // if s in ("moderate", "medium", "default"): return 2
    if (s === 'moderate' || s === 'medium' || s === 'default') return 2;

    // if s in ("strong", "high"): return 3
    if (s === 'strong' || s === 'high') return 3;

    // Accept common variations (client-side safety)
    if (s === 'med' || s === 'normal' || s === 'standard') return 2;

    // If unknown string, fall back to 2 (server defaults to 2 when missing)
    return 2;
  }

  private clampSize(n: number): ImplantSize {
    if (!Number.isFinite(n)) return 2;
    if (n <= 1) return 1;
    if (n === 2) return 2;
    return 3;
  }

  /**
   * Accepts either a data URL or raw base64 and returns raw base64.
   */
  private stripDataUrlIfPresent(s: string): string {
    const raw = String(s || '').trim();
    if (!raw) {
      throw new Error('No image provided.');
    }

    const marker = ';base64,';
    if (raw.startsWith('data:') && raw.includes(marker)) {
      return raw.split(marker, 2)[1].trim();
    }

    return raw;
  }

  private buildHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
    });
  }
}

